import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, Grid, Typography, useMediaQuery } from "@mui/material";
import { styled } from "@mui/material/styles";
import React from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Login_Bg from "src/assets/Login_Bg"; // Background Image
import Logo from "src/assets/Logo";
import { useSnackbar } from "src/context/ShowMessage";
import CustomLoader from "src/shared_components/CustomLoader";
import { InputField } from "src/shared_components/FormElements";
import * as yup from "yup";
import { useAuth } from "../auth/AuthContext";
import withDynamicTitle from "../hocs/withDynamicTitle";
import { LoginFormData } from "../shared_components/interfaces";
import { getPublicSubscriptionStatus } from "../apis/ClientAPI";
import SubscriptionLockedPage from "./SubscriptionLockedPage";

interface AuthenticationProps {}

const RootStyle = styled("div")(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    display: "flex",
    height: "100vh",
  },
}));

const HeaderStyle = styled("header")(({ theme }) => ({
  top: 0,
  left: 0,
  zIndex: 9,
  lineHeight: 0,
  width: 250,
  height: 80,
  display: "flex",
  alignItems: "center",
  position: "absolute",
  padding: theme.spacing(3),
  justifyContent: "space-between",
  [theme.breakpoints.up("lg")]: {
    alignItems: "flex-start",
  },
  // backgroundColor: "black",
  boxShadow: "-3px 7px 40px 19px #0000000D",
  borderBottomRightRadius: "49px",
}));

const Authentication: React.FC<AuthenticationProps> = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const { displayMessage } = useSnackbar();
  const isUpLg = useMediaQuery((theme: any) => theme.breakpoints.up("lg"));

  const [isLocked, setIsLocked] = React.useState<boolean>(false);
  const [lockedDetails, setLockedDetails] = React.useState<{
    clientId: number;
    overdueAmount: number;
    overduePeriodDays: number;
    currency: string;
    currencySymbol: string;
  } | null>(null);
  const [isLoadingCheck, setIsLoadingCheck] = React.useState<boolean>(true);

  React.useEffect(() => {
    const checkStatus = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const clientQuery = params.get("client") || params.get("clientCode");
        let clientCode = clientQuery;

        if (!clientCode) {
          const host = window.location.hostname;
          const parts = host.split(".");
          if (parts.length > 2 && parts[0] !== "www") {
            clientCode = parts[0];
          }
        }

        if (!clientCode) {
          clientCode = "2"; // fallback for testing
        }

        const res = await getPublicSubscriptionStatus(clientCode);
        if (res.status === 200 && res.data) {
          // if (res.data.isSubscriptionLocked) {
          //   setIsLocked(true);
          //   setLockedDetails({
          //     clientId: res.data.clientId,
          //     overdueAmount: res.data.overdueAmount,
          //     overduePeriodDays: res.data.overduePeriodDays,
          //     currency: res.data.currency,
          //     currencySymbol: res.data.currencySymbol,
          //   });
          // }
        }
      } catch (error) {
        console.error("Error checking subscription status:", error);
      } finally {
        setIsLoadingCheck(false);
      }
    };
    checkStatus();
  }, []);

  const schema = yup.object().shape({
    userName: yup.string().required(t("login.validation.usernameRequired")),
    userPassword: yup.string().required(t("login.validation.passwordRequired")),
  });

  const methods = useForm<LoginFormData>({
    resolver: yupResolver(schema),
  });

  const {
    handleSubmit,
    control,
    formState: { isSubmitting, errors },
  } = methods;

  const onSubmit: SubmitHandler<LoginFormData> = async (data) => {
    const isSuccess: boolean | undefined = await auth.signIn(data);
    if (!!isSuccess) {
      navigate("/Dashboard");
    } else {
      displayMessage(t("login.errorMessage"), "error");
    }
  };

  if (isLoadingCheck) {
    return <CustomLoader />;
  }

  // if (isLocked && lockedDetails) {
  //   return (
  //     <SubscriptionLockedPage
  //       clientId={lockedDetails.clientId}
  //       overdueAmount={lockedDetails.overdueAmount}
  //       overduePeriodDays={lockedDetails.overduePeriodDays}
  //       currency={lockedDetails.currency}
  //       currencySymbol={lockedDetails.currencySymbol}
  //       onLogout={() => setIsLocked(false)}
  //     />
  //   );
  // }

  return (
    <RootStyle>
      <HeaderStyle>
        <Logo width={250} height={50} />
      </HeaderStyle>
      <Grid container>
        <Grid
          item
          xl={3}
          lg={3}
          md={12}
          xs={12}
          component="main"
          maxWidth="xs"
          sx={{
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginLeft: { lg: "5%", xs: "0%" },
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              backgroundColor: "primary.main",
              padding: "30px",
              borderRadius: "10px",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
              width: "400px",
            }}
          >
            <Typography
              component="h1"
              variant="h5"
              sx={{ color: "primary.contrastText", fontWeight: "bold" }}
            >
              {t("login.title")}
            </Typography>
            <Typography
              sx={{
                color: "primary.contrastText",
                fontSize: "14px",
                marginBottom: 2,
              }}
            >
              {t("login.welcomeMessage")}
            </Typography>

            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <InputField
                  fieldName="userName"
                  label={t("login.username")}
                  labelColor="primary.contrastText"
                  error={!!errors?.userName}
                  control={control}
                  helperText={errors?.userName?.message}
                  fullWidth
                  other={{
                    sx: {
                      borderRadius: "8px",
                      color: "primary.contrastText",
                    },
                  }}
                  style={{ backgroundColor: "primary.contrastText" }}
                />
                <InputField
                  fieldName="userPassword"
                  label={t("login.password")}
                  labelColor="primary.contrastText"
                  error={!!errors?.userPassword}
                  control={control}
                  helperText={errors?.userPassword?.message}
                  fullWidth
                  style={{ backgroundColor: "primary.contrastText" }}
                  other={{
                    sx: {
                      borderRadius: "8px",
                      color: "primary.contrastText",
                    },
                    type: "password",
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="secondary"
                  sx={{
                    fontWeight: "bold",
                    mt: 3,
                  }}
                >
                  {isSubmitting ? <CustomLoader size={30} /> : t("login.button")}
                </Button>
              </form>
            </FormProvider>
          </Box>
        </Grid>

        {isUpLg && (
          <Grid
            xl={9}
            lg={9}
            md={12}
            xs={12}
            item
            sx={{
              // width: "35%",
              height: "100vh",
              backgroundSize: "contain",
              backgroundPosition: "center",
              position: "absolute",
              right: 0,
              top: 0,
              bottom: 0,
              overflow: "hidden",
            }}
          >
            <Login_Bg width="100%" height="100vh" />
          </Grid>
        )}
      </Grid>
    </RootStyle>
  );
};

export default withDynamicTitle(Authentication, "login.title");
